import { apiRequest } from '@/api/http/fetch'

export interface AuthConfigResponse {
  registration_open: boolean
  require_email_verification: boolean
}

export function verifyEmail(token: string): Promise<void> {
  return apiRequest<void>('/api/v1/auth/verify-email', {
    method: 'POST',
    body: JSON.stringify({ token }),
  })
}

export interface LoginRequest {
  email: string
  password: string
}

export interface LoginResponse {
  access_token: string
  refresh_token: string
  user_id: number
  email: string
  roles: string[]
  '2fa_required'?: boolean
  '2fa_methods'?: string[]
  '2fa_trusted'?: boolean
}

export interface MeResponse {
  user_id: number
  email: string
  first_name?: string
  last_name?: string
  full_name?: string
  avatar_url?: string
  roles: string[]
  permissions?: string[]
  modules_effective?: string[]
  deny_reasons?: string[]
  license_valid?: boolean
  email_verified?: boolean
  email_verification_available?: boolean
  is_active?: boolean
  needs_approval?: boolean
}

export function login(data: LoginRequest): Promise<LoginResponse> {
  return apiRequest<LoginResponse>('/api/v1/auth/login', {
    method: 'POST',
    body: JSON.stringify(data),
  })
}

export function loginWith2FA(data: {
  email: string
  password: string
  code: string
  method?: string
  trust_device?: boolean
}): Promise<LoginResponse> {
  return apiRequest<LoginResponse>('/api/v1/auth/login/2fa', {
    method: 'POST',
    body: JSON.stringify(data),
  })
}

export function refresh(refreshToken: string): Promise<LoginResponse> {
  return apiRequest<LoginResponse>('/api/v1/auth/refresh', {
    method: 'POST',
    body: JSON.stringify({ refresh_token: refreshToken }),
  })
}

export function getMe(): Promise<MeResponse> {
  return apiRequest<MeResponse>('/api/v1/auth/me')
}

// ==================== 2FA ====================

export interface TwoFAStatus {
  enabled: boolean
}

export interface TwoFASetupResult {
  secret: string
  uri: string
}

export interface BackupCode {
  id: number
  used: boolean
}

export interface BackupCodesResponse {
  codes: BackupCode[]
  remaining: number
  total: number
}

export interface RegeneratedBackupCodesResponse {
  codes: string[]
  message: string
}

export function get2FAStatus(): Promise<TwoFAStatus> {
  return apiRequest<TwoFAStatus>('/api/v1/auth/2fa/status')
}

export function setup2FA(): Promise<TwoFASetupResult> {
  return apiRequest<TwoFASetupResult>('/api/v1/auth/2fa/setup', { method: 'POST' })
}

export function enable2FA(code: string): Promise<void> {
  return apiRequest<void>('/api/v1/auth/2fa/enable', {
    method: 'POST',
    body: JSON.stringify({ code }),
  })
}

export function disable2FA(): Promise<void> {
  return apiRequest<void>('/api/v1/auth/2fa/disable', { method: 'POST' })
}

export function getBackupCodes(): Promise<BackupCodesResponse> {
  return apiRequest<BackupCodesResponse>('/api/v1/auth/2fa/backup-codes')
}

export function regenerateBackupCodes(): Promise<RegeneratedBackupCodesResponse> {
  return apiRequest<RegeneratedBackupCodesResponse>('/api/v1/auth/2fa/backup-codes/regenerate', { method: 'POST' })
}

export function getAuthConfig(): Promise<AuthConfigResponse> {
  return apiRequest<AuthConfigResponse>('/api/v1/auth/config')
}

export function logout(refreshToken: string): Promise<void> {
  return apiRequest<void>('/api/v1/auth/logout', {
    method: 'POST',
    body: JSON.stringify({ refresh_token: refreshToken }),
  })
}

export function forgotPassword(email: string): Promise<void> {
  return apiRequest<void>('/api/v1/auth/password/forgot', {
    method: 'POST',
    body: JSON.stringify({ email }),
  })
}

export function resetPassword(token: string, password: string): Promise<void> {
  return apiRequest<void>('/api/v1/auth/password/reset', {
    method: 'POST',
    body: JSON.stringify({ token, password, password_confirm: password }),
  })
}
